import React, { useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner } from "reactstrap";
import { useForm } from "react-hook-form";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import { parseLocalDate } from "common/service/CommonService";
import { checkInFields, checkOutFields, remarksFields } from "../helper/ambulatoryvisitconstants";

const convert12to24 = (time12h?: string): string => {
  if (!time12h) return "";
  const trimTime = time12h.trim().toUpperCase();
  const match = trimTime.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/);
  if (!match) {
    return trimTime.slice(0, 5);
  }
  let [_, hoursStr, minutesStr, modifier] = match;
  let hours = parseInt(hoursStr, 10);
  if (modifier === "PM" && hours < 12) {
    hours += 12;
  }
  if (modifier === "AM" && hours === 12) {
    hours = 0;
  }
  return `${String(hours).padStart(2, "0")}:${minutesStr}`;
};

const convert24to12 = (time24h?: string): string => {
  if (!time24h) return "";
  const match = time24h.trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) return time24h;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const modifier = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  return `${String(hours).padStart(2, "0")}:${minutes} ${modifier}`;
};

const display12h = (timeStr?: string): string => {
  if (!timeStr) return "";
  if (timeStr.toUpperCase().includes("AM") || timeStr.toUpperCase().includes("PM")) {
    return timeStr;
  }
  return convert24to12(timeStr);
};

interface AmbulatoryCheckInModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: any) => void;
  visitCode?: string;
  subjectInfo?: string;
  mode: "checkin" | "checkout";
  isPending?: boolean;
  scheduledDate?: string;
  visitStartDay?: any;
  visitEndDay?: any;
  checkinDate?: string;
  checkinTime?: string;
}

const AmbulatoryCheckInModal: React.FC<AmbulatoryCheckInModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  subjectInfo,
  mode,
  isPending = false,
  scheduledDate,
  visitStartDay,
  visitEndDay,
  checkinDate,
  checkinTime,
}) => {
  const isCheckOut = mode === "checkout";

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors }
  } = useForm<any>({
    defaultValues: {
      checkinDate: "",
      checkinTime: "",
      checkoutDate: "",
      checkoutTime: "",
      remarks: "",
    },
    mode: "onChange"
  });

  useEffect(() => {
    if (isOpen) {
      const today = new Date().toISOString().split("T")[0];
      const nowTime = new Date().toTimeString().slice(0, 5);

      let defaultCheckInDate = today;
      if (scheduledDate) {
        const startOffset = Number(visitStartDay || 0);
        const endOffset = Number(visitEndDay || 0);

        const minDateLimit = parseLocalDate(scheduledDate);
        if (minDateLimit) {
          minDateLimit.setHours(0, 0, 0, 0);
          minDateLimit.setDate(minDateLimit.getDate() + startOffset);
        }

        const maxDateLimit = parseLocalDate(scheduledDate);
        if (maxDateLimit) {
          maxDateLimit.setHours(0, 0, 0, 0);
          maxDateLimit.setDate(maxDateLimit.getDate() + endOffset);
        }

        const todayObj = new Date();
        todayObj.setHours(0, 0, 0, 0);

        if (minDateLimit && maxDateLimit && todayObj >= minDateLimit && todayObj <= maxDateLimit) {
          defaultCheckInDate = today;
        } else {
          const parsedSch = parseLocalDate(scheduledDate);
          defaultCheckInDate = parsedSch ? parsedSch.toISOString().split("T")[0] : today;
        }
      }

      let defaultCheckOutDate = today;
      if (checkinDate) {
        try {
          const parsedCI = parseLocalDate(checkinDate);

          defaultCheckOutDate = parsedCI
            ? `${parsedCI.getFullYear()}-${String(parsedCI.getMonth() + 1).padStart(2, "0")}-${String(parsedCI.getDate()).padStart(2, "0")}`
            : today;
        } catch (e) {
          defaultCheckOutDate = checkinDate.split("T")[0];
        }
      }

      reset({
        checkinDate: defaultCheckInDate,
        checkinTime: nowTime,
        checkoutDate: defaultCheckOutDate,
        checkoutTime: nowTime,
        remarks: "",
      });
    }
  }, [isOpen, reset, scheduledDate, visitStartDay, visitEndDay, checkinDate]);

  const handleSave = (values: any) => {
    onConfirm(values);
  };

  const startOffset = Number(visitStartDay || 0);
  const endOffset = Number(visitEndDay || 0);

  const minD = scheduledDate ? (() => {
    const d = parseLocalDate(scheduledDate);
    if (d) {
      d.setDate(d.getDate() + startOffset);
    }
    return d || undefined;
  })() : undefined;

  const maxD = scheduledDate ? (() => {
    const d = parseLocalDate(scheduledDate);
    if (d) {
      d.setDate(d.getDate() + endOffset);
    }
    return d || undefined;
  })() : undefined;

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose} className="fw-bold border-bottom p-3">
        <div className="d-flex align-items-center gap-2">
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: isCheckOut ? "#eafaf1" : "#eff6ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i className={`${isCheckOut ? "ri-logout-box-line text-success" : "ri-login-box-line text-primary"} fs-18`} />
          </div>
          <div>
            <h5 className="modal-title mb-0 fw-bold">
              {isCheckOut ? "Check-Out Subject" : "Check-In Subject"}
            </h5>
            <span className="text-muted" style={{ fontSize: 12 }}>
              {subjectInfo || "Subject"}
            </span>
          </div>
        </div>
      </ModalHeader>

      <ModalBody>
        <CommonForm
          fields={isCheckOut ? checkOutFields(convert12to24,display12h,checkinTime) : checkInFields(minD, maxD)}
          control={control}
          errors={errors}
          columns={2}
        />
        <div className="mt-3">
          <CommonForm
            fields={remarksFields}
            control={control}
            errors={errors}
            columns={1}
          />
        </div>
      </ModalBody>

      <ModalFooter className="border-top">
        <Button color="dark" outline onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleSubmit(handleSave)} disabled={isPending}>
          {isPending && <Spinner size="sm" className="me-2" />}
          <i className={`${isCheckOut ? "ri-logout-box-line" : "ri-login-box-line"} me-1`} /> 
          {isCheckOut ? "Check-Out" : "Check-In"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AmbulatoryCheckInModal;
